import React, { useEffect, useMemo, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { LE_BUSINESS_TRAVELLER } from '~/consts/businessTraveller';

import { useListUsers } from '~/hooks/apis/agent/useListUsers';
import { useGetPnrDiscoveryDetails } from '~/hooks/apis/flights/usePnrDiscoveries';
import { useCreatePnrImportOrder } from '~/hooks/apis/orders/useCreatePnrImportOrder';

import type { PnrDiscovery } from '~/services/FlightsService';

import BusinessEmployeeSelect from './BusinessEmployeeSelect';
import BusinessSelect from './BusinessSelect';
import ConfirmPaidPnrImportDialog from './ConfirmPaidPnrImportDialog';
import LebtServiceFeePicker from './LebtServiceFeePicker';
import PnrImportPnrCard from './PnrImportPnrCard';
import {
  type HandlingFees,
  PAID_REFERENCE_MAX_LENGTH,
  areValidHandlingFees,
  buildHandlingFees,
  buildPaidMarker,
  buildPnrImportOrderRequest,
  calculateAmountToPay,
  calculateCustomerPrice,
  calculateHandlingFeeTotal,
  getPnrAncillaries,
  getPnrBookingTotals,
  getPnrImportFailures,
  isValidMargin,
  parseMargin,
} from './pnrImport';

const CUSTOMER_SEARCH_MIN_LENGTH = 3;

interface Customer {
  id_member: string;
  email?: string;
}

interface Props {
  discoveries: Array<PnrDiscovery>;
  brand: string;
  onClose: () => void;
  onImported: (orderId: string) => void;
}

/** A failed import is rolled back and its order cancelled, so every attempt needs its own keys. */
const generateTransactionKeys = (pnrs: Array<string>) => ({
  order: uuidv4(),
  items: Object.fromEntries(pnrs.map((pnr) => [pnr, uuidv4()])),
});

/**
 * Imports the selected PNRs into one order, unpaid unless the agent marks it as already paid, in which case the
 * customer is not asked to pay and the order is confirmed straight away. The customer price of each PNR is its
 * provider cost plus the agent's margin, which starts at the markup Sabre holds on the PNR, plus the handling fee the
 * agent sets for each seat booked on the PNR. An import that fails is
 * rolled back by svc-order, so a retry starts over with new transaction keys and the same PNRs can be imported
 * together again.
 */
export default function PnrImportDialog({ discoveries, brand, onClose, onImported }: Props) {
  const isBusinessTraveller = brand === LE_BUSINESS_TRAVELLER;
  const pnrs = useMemo(() => discoveries.map((discovery) => discovery.pnr_id), [discoveries]);
  const details = useGetPnrDiscoveryDetails(pnrs);
  const createOrder = useCreatePnrImportOrder();

  const [transactionKeys, setTransactionKeys] = useState(() => generateTransactionKeys(pnrs));
  const [margins, setMargins] = useState<Record<string, string>>({});
  const [handlingFees, setHandlingFees] = useState<Record<string, HandlingFees>>({});
  const [customerSearch, setCustomerSearch] = useState(discoveries[0]?.contact_email ?? '');
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [business, setBusiness] = useState<App.Business | null>(null);
  const [bookingManager, setBookingManager] = useState<App.BusinessEmployee | null>(null);
  const [travellerProfiles, setTravellerProfiles] = useState<
    Record<string, Record<string, App.BusinessEmployee | null>>
  >({});
  const [serviceFee, setServiceFee] = useState<App.LebtCustomOfferServiceFee | undefined>();
  const [isPaid, setIsPaid] = useState(false);
  const [paidReference, setPaidReference] = useState('');
  const [isConfirmingPaid, setIsConfirmingPaid] = useState(false);

  const customerResults = useListUsers(
    customerSearch.trim().length >= CUSTOMER_SEARCH_MIN_LENGTH ? customerSearch.trim() : '',
    brand,
  );

  const bookingsKey = details.map((detail) => detail.dataUpdatedAt).join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- recomputed when any booking finishes loading
  const bookings = useMemo(() => details.map((detail) => detail.data?.booking), [bookingsKey]);
  const totals = useMemo(() => bookings.map((booking) => getPnrBookingTotals(booking)), [bookings]);
  const currencies = [...new Set(totals.map((total) => total.currency).filter(Boolean))];
  const currency = currencies[0];

  // Start each margin at the markup Sabre holds on the PNR, once its booking has loaded.
  useEffect(() => {
    setMargins((current) => {
      const prefilled = Object.fromEntries(
        pnrs.flatMap((pnr, index) => {
          if (current[pnr] !== undefined || !totals[index].providerCost) {
            return [];
          }
          return [[pnr, totals[index].providerMarkup.toFixed(2)]];
        }),
      );
      if (!Object.keys(prefilled).length) {
        return current;
      }
      return { ...current, ...prefilled };
    });
  }, [pnrs, totals]);

  const isLoadingDetails = details.some((detail) => detail.isLoading);
  const hasMixedCurrencies = currencies.length > 1;
  const hasValidMargins = pnrs.every((pnr, index) => isValidMargin(margins[pnr] ?? '', totals[index].providerCost));
  const hasValidHandlingFees = pnrs.every((pnr) => areValidHandlingFees(handlingFees[pnr] ?? {}));
  // Business Traveller imports must go through svc-business, so only Luxury Escapes imports are submitted here.
  const canSubmit =
    !isBusinessTraveller &&
    !isLoadingDetails &&
    !hasMixedCurrencies &&
    !!currency &&
    hasValidMargins &&
    hasValidHandlingFees &&
    !!customer &&
    !createOrder.isPending;

  const failures = getPnrImportFailures(createOrder.error);

  const prices = pnrs.map((pnr, index) =>
    calculateCustomerPrice(
      totals[index].providerCost,
      parseMargin(margins[pnr] ?? '') ?? 0,
      calculateHandlingFeeTotal(handlingFees[pnr] ?? {}),
    ),
  );
  const amountToPay = calculateAmountToPay(prices, isPaid);
  const paid = buildPaidMarker(isPaid, paidReference);

  const importOrder = () => {
    setIsConfirmingPaid(false);
    if (!canSubmit || !customer || !currency) {
      return;
    }
    const orderRequest = buildPnrImportOrderRequest({
      orderTransactionKey: transactionKeys.order,
      brand,
      currency,
      customerId: customer.id_member,
      pnrs: pnrs.map((pnr, index) => ({
        pnr,
        transactionKey: transactionKeys.items[pnr],
        price: prices[index],
        handlingFees: buildHandlingFees(handlingFees[pnr] ?? {}, getPnrAncillaries(bookings[index])),
      })),
      paid,
    });
    createOrder.mutate(orderRequest, {
      onSuccess: (order) => onImported(order.id),
      onError: () => setTransactionKeys(generateTransactionKeys(pnrs)),
    });
  };

  // An order marked as already paid is confirmed straight away, so the agent confirms it first.
  const submit = () => {
    if (isPaid) {
      setIsConfirmingPaid(true);
      return;
    }
    importOrder();
  };

  let submitLabel = 'Create unpaid order';
  if (isPaid) {
    submitLabel = 'Create paid order';
  }

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import {pnrs.length === 1 ? 'PNR' : `${pnrs.length} PNRs`} into one order</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {hasMixedCurrencies && (
            <Alert severity="error">
              The selected PNRs are priced in different currencies and cannot share an order.
            </Alert>
          )}

          {!isBusinessTraveller && (
            <Autocomplete
              size="small"
              options={(customerResults.data ?? []) as Array<Customer>}
              value={customer}
              onChange={(_event, selected) => setCustomer(selected)}
              inputValue={customerSearch}
              onInputChange={(_event, text) => setCustomerSearch(text)}
              getOptionLabel={(option) => `${option.email ?? option.id_member} (${option.id_member})`}
              isOptionEqualToValue={(option, selected) => option.id_member === selected.id_member}
              filterOptions={(options) => options}
              loading={customerResults.isFetching}
              renderInput={(params) => <TextField {...params} label="Customer (search by email or name)" required />}
            />
          )}

          {isBusinessTraveller && (
            <Stack spacing={2}>
              <Alert severity="info">
                Business Traveller imports are not available yet. The booking manager, traveller profiles and service
                fee can be reviewed, but the order cannot be created.
              </Alert>
              <BusinessSelect
                value={business}
                onChange={(selected) => {
                  setBusiness(selected);
                  setBookingManager(null);
                  setTravellerProfiles({});
                  setServiceFee(undefined);
                }}
              />
              {business && (
                <>
                  <BusinessEmployeeSelect
                    key={business.id}
                    businessId={business.id}
                    label="Booking manager"
                    value={bookingManager}
                    onChange={setBookingManager}
                  />
                  <LebtServiceFeePicker
                    key={`fee-${business.id}`}
                    businessId={business.id}
                    value={serviceFee}
                    onChange={setServiceFee}
                  />
                </>
              )}
            </Stack>
          )}

          {pnrs.map((pnr, index) => (
            <PnrImportPnrCard
              key={pnr}
              pnr={pnr}
              booking={bookings[index]}
              isLoading={details[index].isLoading}
              isError={details[index].isError}
              margin={margins[pnr] ?? ''}
              onMarginChange={(margin) => setMargins((current) => ({ ...current, [pnr]: margin }))}
              handlingFees={handlingFees[pnr] ?? {}}
              onHandlingFeeChange={(ancillaryId, fee) =>
                setHandlingFees((current) => ({ ...current, [pnr]: { ...current[pnr], [ancillaryId]: fee } }))
              }
              onHandlingFeesChange={(fees) =>
                setHandlingFees((current) => ({ ...current, [pnr]: { ...current[pnr], ...fees } }))
              }
              businessId={isBusinessTraveller ? business?.id : undefined}
              travellerProfiles={travellerProfiles[pnr] ?? {}}
              onTravellerProfileChange={(travellerId, employee) =>
                setTravellerProfiles((current) => ({
                  ...current,
                  [pnr]: { ...current[pnr], [travellerId]: employee },
                }))
              }
              isPaid={isPaid}
              disabled={createOrder.isPending}
            />
          ))}

          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPaid}
                  onChange={(event) => setIsPaid(event.target.checked)}
                  disabled={createOrder.isPending}
                />
              }
              label="This order was already paid"
            />
            {isPaid && (
              <>
                <TextField
                  size="small"
                  label="Payment reference (optional)"
                  value={paidReference}
                  onChange={(event) => setPaidReference(event.target.value)}
                  slotProps={{ htmlInput: { maxLength: PAID_REFERENCE_MAX_LENGTH } }}
                  disabled={createOrder.isPending}
                  sx={{ maxWidth: 360 }}
                />
                <Alert severity="warning">
                  The customer will not be asked to pay. The order is confirmed and ticketed straight away. No loyalty
                  points are earned and the order cannot be refunded or cancelled from the import grid.
                  {isBusinessTraveller && ' The service fee is not charged either.'}
                </Alert>
              </>
            )}
          </Stack>

          {!isBusinessTraveller && !!currency && hasValidMargins && (
            <Typography variant="subtitle1">
              Amount the customer pays: {currency} {amountToPay.toFixed(2)}
              {isPaid && ' (already paid)'}
            </Typography>
          )}

          {createOrder.isError && (
            <Alert severity="error">
              {failures.length > 0 && (
                <>
                  <Typography variant="body2">These PNRs could not be imported, so no order was created:</Typography>
                  {failures.map((failure) => (
                    <Typography key={failure.pnr} variant="body2">
                      {failure.pnr}: {failure.message}
                    </Typography>
                  ))}
                </>
              )}
              {failures.length === 0 && createOrder.error.message}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={submit} disabled={!canSubmit}>
          {submitLabel}
        </Button>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
      {isConfirmingPaid && customer && (
        <ConfirmPaidPnrImportDialog
          customerLabel={customer.email ?? customer.id_member}
          pnrs={pnrs}
          reference={paid?.reference}
          isPending={createOrder.isPending}
          onConfirm={importOrder}
          onClose={() => setIsConfirmingPaid(false)}
        />
      )}
    </Dialog>
  );
}
